import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { IconSearch } from "@tabler/icons-react";
import { openDesktopSearchPalette } from "../../search/DesktopSearchPaletteHost";
import { cn } from "../../../lib/utils";

/**
 * The bar that tops a desktop page, right of the sidebar (Figma
 * "Desktop/v5/TopNav"). The left side is the page's own context (the practice
 * dropdown on Explore, the breadcrumb of a JuisciGPT conversation); the right
 * side is the search trigger, or the page's own actions where the page is a
 * workspace of its own (a conversation, whose bar holds its panel toggle).
 *
 * The trigger is a button, not an input: it opens the desktop search palette,
 * which ⌘K opens too (bound once by the desktop shell, so the palette stays
 * one keystroke away on a page whose bar does not show the trigger).
 * `search-after` puts a control of the page before the trigger (the pager of
 * an article opened from a list).
 *
 * `overlay` is the bar over a page's banner (a collection's cover): no
 * background and no border, the context and the search in white pills. The
 * page switches back to `default` once its content scrolls under the bar.
 */
function DesktopTopNav({
  context,
  trailing,
  surface,
  className,
}: {
  context: ReactNode;
  trailing:
    | { kind: "search" }
    | { kind: "search-after"; leading: ReactNode }
    | { kind: "actions"; content: ReactNode };
  surface: "default" | "overlay";
  className: string | null;
}): React.JSX.Element {
  return (
    <header
      className={cn(
        "sticky top-0 z-10 flex h-14 shrink-0 items-center gap-6 border-b px-6 transition-colors",
        surface === "overlay"
          ? "border-transparent bg-transparent"
          : "border-ds-slate-100 bg-ds-white",
        className
      )}
    >
      <div className="flex min-w-0 flex-1 items-center">
        {surface === "overlay" ? (
          // The search trigger's height, so the two pills line up.
          <div className="flex h-9 min-w-0 items-center rounded-lg bg-ds-cream pr-3.5 pl-2.5 shadow-xs backdrop-blur-sm">
            {context}
          </div>
        ) : (
          context
        )}
      </div>
      {(() => {
        switch (trailing.kind) {
          case "search":
            return <SearchTrigger surface={surface} />;
          case "search-after":
            return (
              <div className="flex shrink-0 items-center gap-4">
                {trailing.leading}
                <SearchTrigger surface={surface} />
              </div>
            );
          case "actions":
            return (
              <div className="flex shrink-0 items-center gap-3">
                {trailing.content}
              </div>
            );
          default:
            throw new Error(trailing satisfies never);
        }
      })()}
    </header>
  );
}

function SearchTrigger({
  surface,
}: {
  surface: "default" | "overlay";
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={(e) => {
        openDesktopSearchPalette(e.currentTarget);
      }}
      className={cn(
        "flex h-9 w-70 shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 text-left text-ds-slate-500 transition-colors",
        surface === "overlay"
          ? "bg-ds-cream shadow-xs backdrop-blur-lg hover:bg-ds-slate-100"
          : "bg-ds-slate-100 hover:bg-ds-slate-200"
      )}
    >
      <IconSearch size={14} stroke={2} className="shrink-0" />
      <span className="min-w-0 flex-1 truncate text-footnote">
        {t("Search articles, Rooms, topics")}
      </span>
      <kbd className="shrink-0 font-dm-sans text-xs">⌘K</kbd>
    </button>
  );
}

export { DesktopTopNav };
